import { Injectable } from "@angular/core";
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from "@angular/common/http";
import { environment } from "environments/environment.staging";
import { from, Observable, throwError } from "rxjs";
import { Router } from "@angular/router";
import { catchError, retry, tap } from "rxjs/operators";

@Injectable({
  providedIn: "root",
})
export class ApisService {
  private readonly API_BASE_URL = environment.apiUrl;

  // Function To extract only the token from the header
  Header(): string {
    const headers = this.createHeaders();
    const token = headers.get("Authorization"); // Extract token
    return token;
  }

  chartFunction(url): Observable<any> {
    const headers = this.createHeaders();
    const token = headers.get("Authorization"); // Extract token
    return this.http.get(url, {
      headers: { Authorization: token ? token : "" },
    });
  }

  constructor(private http: HttpClient, private router: Router) {}

  /*** 🔹 PRIVATE UTILITY METHODS 🔹 ***/

  private createHeaders(): HttpHeaders {
    const currentUser = localStorage.getItem("currentUser");
    const token = currentUser
      ? JSON.parse(currentUser)?.token
      : "default_token";

    return new HttpHeaders({
      Accept: "application/json",
      "Accept-Language": "en",
      Authorization: `Bearer ${token}`,
    });
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    console.error("API Error:", error);
    if (error.status === 401 || error.error?.message === "Unauthenticated.") {
      this.router.navigate(["pages/authentication/login-v2"]);
    }
    return throwError(error);
  }

  private request(
    method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
    endpoint: string,
    data?: any,
    params?: HttpParams
  ): Observable<any> {
    let headers = this.createHeaders();
    if (data instanceof FormData) headers = headers.delete("Content-Type");

    return this.http
      .request(method, `${this.API_BASE_URL}${endpoint}`, {
        body: ["POST", "PUT", "PATCH"].includes(method) ? data : null,
        headers,
        params: params || new HttpParams(),
      })
      .pipe(
        tap((response) => console.log(`[${method}] ${endpoint} =>`, response)),
        catchError(this.handleError.bind(this))
      );
  }

  /*** 🔹 GENERIC API METHODS 🔹 ***/

  get(endpoint: string, params?: HttpParams): Observable<any> {
    return this.request("GET", endpoint, null, params);
  }

  post(endpoint: string, data: any): Observable<any> {
    return this.request("POST", endpoint, data);
  }

  put(endpoint: string, data: any): Observable<any> {
    return this.request("PUT", endpoint, data);
  }

  patch(endpoint: string, data: any): Observable<any> {
    return this.request("PATCH", endpoint, data);
  }

  delete(endpoint: string): Observable<any> {
    return this.request("DELETE", endpoint);
  }

  /*** 🔹 SPECIFIC API ENDPOINTS 🔹 ***/

  

/*=============================================
=            Media APi Section            =
=============================================*/
presignedUrl(data: any): Observable<any> {
  return this.post(`/media-persigned-url`, data);
 }


 MediaUpload(presignedUrl:any , data: any): Observable<any> {
  return this.http.put(presignedUrl, data, {
    headers: {
      'Content-Type': data.type || 'application/octet-stream'
    }
  });
 }


/*=====  End of Media APi Section  ======*/


/*=============================================
=            Subscription Plans API            =
=============================================*/
getSubplans(): Observable<any> {
  return this.get(`/subscription-plans`);
}

addSubPlan(data: any): Observable<any> {
  return this.post(`/subscription-plans`, data);
}

deleteSub(id: string): Observable<any> {
  return this.delete(`/subscription-plans/${id}`);
}

cancelReason() : Observable<any> {
  return this.get(`/subscriptions/cancel-reasons`);
}
deleteCancelReason(id: string): Observable<any> {
  return this.delete(`/subscriptions/cancel-reasons/${id}`);
}
editCancelReason(id: string, data: any): Observable<any> {
  return this.patch(`/subscriptions/cancel-reasons/${id}`, data);
} 

addCancelReason(data : any ) : Observable<any> {
  return this.post(`/subscriptions/cancel-reasons`, data);
}

cancelReasonById(id: string): Observable<any> {
  return this.get(`/subscriptions/cancel-reasons/${id}`);
}

  /*----------  User Gino Token  ----------*/
  userGinoToken(id : any): Observable<any> {
    return this.get(`/subscriptions/tokens/summary?userId=${id}`);
  }

  checkUserSubscription(id : any): Observable<any> {
    return this.get(`/subscriptions/check-user-subscription?userId=${id}`);
  }


/*=====  End of Subscription Plans API  ======*/


/*=============================================
=            Subscription Types API            =
=============================================*/
getSubTypes(): Observable<any> {
  return this.get(`/subscription-types`);
}
getSubTypesById(id:any): Observable<any> {
  return this.get(`/subscription-types/${id}`);
}

addSubTypes(data : any) : Observable<any> {
  return this.post(`/subscription-types`, data);
}

editSubTypes(id: string, data: any): Observable<any> {
  return this.put(`/subscription-types/${id}`, data);
}

deleteSubTypes(id: string): Observable<any> {
  return this.delete(`/subscription-types/${id}`);
}

getSubTypePlan(id : any) : Observable<any> {
  return this.get(`/subscription-plans/by-type/${id}`);
}

addFeature(id :any , data : any) : Observable<any> {
  return this.patch(`/subscription-types/${id}/features`, data);

}

deleteFeature(id : any ) : Observable<any> {
  return this.delete(`/subscription-types/${id}/features`);
}



/*=====  End of Subscription Types API  ======*/



// start of age groups  


getGroupedAge() : Observable<any> {
  return this.get(`/age-groups`);
}

addGroupedAge( data : any) : Observable<any> {
  return this.post(`/age-groups`, data);

}

deleteGroupedAge(id : any ) : Observable<any> {
  return this.delete(`/age-groups/${id}`);
}


editGroupedAge(id: string, data: any): Observable<any> {
  return this.patch(`/age-groups/${id}`, data);
}

getAgeGroupById(id: string | null): Observable<any> {
  return this.get(`/age-groups/${id}`);
}


// end of age groups  


// start of football facilities  

getFootballFacilities() : Observable<any> {
  return this.get(`/football-facilities`);
}

addFootballFacilities( data : any) : Observable<any> {
  return this.post(`/football-facilities`, data);

}

deleteFootballFacilities(id : any ) : Observable<any> {
  return this.delete(`/goalngo-sports/${id}`);
}



// end of football facilities  


createTransaction(payload: any) {
  return this.post(`/provider-payouts`, payload);
}

// start of sports  

getSports() : Observable<any> {
  return this.get(`/goalngo-sports`);
}

addSports( data : any) : Observable<any> {
  return this.post(`/goalngo-sports`, data);

}

deleteSports(id : any ) : Observable<any> {
  return this.delete(`/goalngo-sports/${id}`);
}



// end of sports  


// start of branches  


getBranches() : Observable<any> {
  return this.get(`/age-groups`);
}

addBranches( data : any) : Observable<any> {
  return this.post(`/age-groups`, data);

}

deleteBranches(id : any ) : Observable<any> {
  return this.delete(`/age-groups/${id}`);
}


editBranches(id: string, data: any): Observable<any> {
  return this.patch(`/age-groups/${id}`, data);
}

getBranchesId(id: string | null): Observable<any> {
  return this.get(`/age-groups/${id}`);
}



// end of branches  


/*=============================================
=            Subscription API            =
=============================================*/
getSubscriptions() : Observable<any> {
  return this.get(`/subscriptions`);
}

deleteSubscription(id : any) : Observable<any> {
  return this.delete(`/subscriptions/${id}`);
}

getSubscriptionById(id : any) : Observable<any> {
  return this.get(`/subscriptions/${id}`);
}


/*=====  End of Subscription API  ======*/









  // 🎯 **User Management**
  getUserProfile(id: string): Observable<any> {
    return this.get(`/users/${id}/v1`);
  }

  register(data: any): Observable<any> {
    return this.post(`/api/method/ciqonia_api_app.api.registration`, data);
  }

  getTags(offset: number, limit: number , title ? :string): Observable<any> {
    let params = new HttpParams();
    if (title) {
      params = params.append('title', title);
    }
    return this.get(`/tags?limit=${limit}&offset=${offset}` , params);
  }

  addNotifications(data: any): Observable<any> {
    return this.post(`/admin/notifications`, data);
  }

  askAI(data: any): Observable<any> {
    return this.post(`/ai/ask-db`, data);
  }

  // 🎯 **Players & Scouts**
  getPlayers(offset = 0, limit = 20, role = "PLAYER"): Observable<any> {
    return this.get(`/v1/users?offset=${offset}&limit=${limit}&role=${role}`);
  }

  getPlayersOrScouts(
    role: string,
    offset: number,
    limit: number
  ): Observable<any> {
    return this.get(`/v1/users?offset=${offset}&limit=${limit}&role=${role}`).pipe(
      retry(3)
    );
  }

  getPlayerById(playerId: number): Observable<any> {
    return this.get(`/v1/users/${playerId}/v1`);
  }



  getCountries(): Observable<any> {
    return this.get(`/countries/`);
  }

  getNationalities(): Observable<any> {
    return this.get(`/nationality/`);
  }
  updatePlayer(playerId: number, playerData: any): Observable<any> {
    return this.patch(`/users/${playerId}`, playerData);
  }

  getContracts(offset: number, limit: number): Observable<any> {
    return this.get(`/api/services/contracts?offset=${offset}&limit=${limit}`);
  }

  getContractDetails(contractId: number): Observable<any> {
    return this.get(`/api/services/contracts/${contractId}`);
  }

  getPlayersallList(
    offset: number,
    limit: number, 
    verificationStatus? :string
  ): Observable<any> {
    let params = new HttpParams();
    if (verificationStatus) {
      params = params.append('verificationStatus', verificationStatus);
    }
    return this.get(`/v1/users?offset=${offset}&limit=${limit}` , params).pipe(
      retry(3)
    );
  }

  searchUsersList(
    searchTerm: string,
    offset: number,
    limit: number
  ): Observable<any> {
    return this.get(
      `/v1/search/users?name=${encodeURIComponent(
        searchTerm
      )}&limit=${limit}&offset=${offset}`
    ).pipe(tap((response) => console.log("API Response:", response)));
  }

  // 🎯 **Football Entities & Skills**
  
  /*=============================================
  =            Entities API            =
  =============================================*/
    getEntities(offset: number, limit: number): Observable<any> {
    return this.get(`/football-entities?limit=${limit}&offset=${offset}`);
  }
  // get pending entities
  getEntitiesByStatus(
    offset: number,
    limit: number,
    status: any, 
    email? : any
  ): Observable<any> {
    let params = new HttpParams();
    if (email) {
      params = params.append('email', email);
    }

    return this.get(
      `/search/football-entities?offset=${offset}&limit=${limit}&status=${status}` , params
    );
  }

  addEntity(data: any): Observable<any> {
    return this.post(`/football-entities`, data);
  }

  updateEntity(id: string, data: any): Observable<any> {
    return this.put(`/football-entities/${id}/admin`, data);
  }

  // Get one entity for edit
  getOneEntity(id: string): Observable<any> {
    return this.get(`/football-entities/${id}/admin`);
  }
  // Fetching pending entities counts
  pendingEntitiesCount(): Observable<any> {
    return this.get(`/pending-counts`);
  }

  entitySearch(offset: any, limit: any,status: any , param? : { search?: string; email?: string}): Observable<any> {
    let params = new HttpParams();
    if (param.search) {
      params = params.append('search', param.search);
    }
    if (param.email) {
      params = params.append('email', param.email);
    }
    return this.get(
      `/search/football-entities?offset=${offset}&limit=${limit}&status=${status}`, params
    );
  }
  addEntityBranch(data: any): Observable<any> {
    return this.post(`/football-entity-branches`, data);
  }
  editEntityBranch(id:any,data: any): Observable<any> {
    return this.patch(`/football-entity-branches/${id}`, data);
  }

  getEntityBranch(id : any) : Observable<any> {
    return this.get(`/football-entity-branches?footballEntityId=${id}`);
  }

  getBranchesByLocation(id:any): Observable<any> {
    return this.get(`/football-entity-branches/group-by-country?footballEntityId=${id}`);
  }
  
  getEntityBranchById(id : any) : Observable<any> {
    return this.get(`/football-entity-branches/${id}`);
  }

  deleteEntityBranch(id : any) : Observable<any> {
    return this.delete(`/football-entity-branches/${id}`);
  }
  /*=====  End of Entities API  ======*/
  
  

  getSkills(status: string, offset: number, limit: number , role: string): Observable<any> {
    return this.get(
      `/skills/admin?limit=${limit}&offset=${offset}&status=${status}&role=${role}`
    );
  }

  deleteSkill(skillId: number): Observable<any> {
    return this.delete(`/skills/${skillId}`);
  }


/*=============================================
=            Posts APi            =
=============================================*/
  // 🎯 **Posts & Media**
  getPosts(limit: number, offset: number, role: string): Observable<any> {
    return this.get(
      `/posts?limit=${limit}&offset=${offset}&isDeleted=false&postType=${role}`
    );
  }
  reportedPosts(limit: number, offset: number): Observable<any> {
    return this.get(`/reports/posts?limit=${limit}&offset=${offset}`);
  }
  // Delete posts
  deletePost(id: string): Observable<any> {
    return this.delete(`/posts/${id}`);
  }

  sendPost(data: any): Observable<any> {
    return this.post(`/posts/basic-post`, data);
  }

  likePost(id: string): Observable<any> {
    return this.patch(`/posts/${id}/like`, {});
  }

  commentPost(data: any): Observable<any> {
    return this.post(`/comments`, data);
  }

  repost(id: string): Observable<any> {
    return this.post(`/posts/${id}/repost`, {});
  }

  getComments(limit : any , offset : any , id:string  ):Observable<any>{
    return this.get(`/comments?limit=${limit}&offset=${offset}&postId=${id}`);
  }


/*=====  End of Posts APi  ======*/



  deleteList(id: any): Observable<any> {
    return this.delete(`/list-entries/${id}`);
  }

  
  /*----------  delete Service  ----------*/
  deleteService(id: any): Observable<any> {
    return this.delete(`/services/${id}`);
  }
  
  
  /*----------  Suspend Service  ----------*/
  suspendService(id:any):Observable<any>{
    return this.delete(`/api/services/${id}`);
  }

  
  /*----------  Approved by admin Service  ----------*/
  serviceStatus(id:any , data:any):Observable<any>{
    return this.patch(`/api/services/${id}/moderate` , data);
  }

  getServiceReview(id: any): Observable<any> {
    return this.get(`/api/services/feedback?serviceId=${id}`);
  }
  
  

  // 🎯 **Admin & Notifications**
  adminDashboard(): Observable<any> {
    return this.get(`/api/dashboard/admin/users-persona-stats`);
  }

  getNotifications(): Observable<any> {
    return this.get(`/admin/notifications`);
  }

  // 🎯 **QR Code Management**
  getQRCode(videoId: string): Observable<any> {
    return this.post(`/api/dashboard-auth-api/get-random-link`, {
      video_id: videoId,
    });
  }

  generateQRCode(formData: any): Observable<any> {
    return this.post(`/api/dashboard-auth-api/generate-random-link`, formData);
  }

  addRole(data: any): Observable<any> {
    return this.post(`/skills`, data);
  }

  sendAddReview(data: any): Observable<any> {
    return this.post(`/review`, data);
  }

  addPostsTags(): Observable<any> {
    return this.get(`/tags?title=&limit=10&offset=0`);
  }

  sendAddPost(data: any): Observable<any> {
    return this.post(`/posts/basic-post`, data);
  }

  sendAddEvent(data: any): Observable<any> {
    return this.post(`/api/events`, data);
  }

  sendAddChallenge(data: any): Observable<any> {
    return this.post(`/challenges`, data);
  }

  // getLists(): Observable<any> {
  //   return this.get(`/list-entry`);
  // }

  getLists(status: string): Observable<any> {
    return this.get(
      `/list-entry?status=${status}`
    );
  }

  getFootballEntities(limit: number = 50, offset: number = 0): Observable<any> {
    return this.get(`/football-entities?limit=${limit}&offset=${offset}`);
  }



  getRequests(): Observable<any> {
    return this.get(`/requests`);
  }

  getChallenge(): Observable<any> {
    return this.get(`/challenges`);
  }

  deleteEntity(skillId: any): Observable<any> {
    return this.delete(`/users/${skillId}`);
  }

  // users list edit

  getUseredit(id: string): Observable<any> {
    return this.get(`/users/players?offset=0&limit=20&role=PLAYER`);
  }

  // getUsereditVerified(id: string): Observable<any> {
  //   return this.get(`/users/players?offset=0&limit=20&role=PLAYER&verificationStatus=VERIFIED` );
  // }
  getUsereditVerified(
    role: string,
    offset: number,
    limit: number
  ): Observable<any> {
    return this.get(
      `/users/players?offset=${offset}&limit=${limit}&role=PLAYER&verificationStatus=VERIFIED`
    );
  }

  getGuardiansVerified(
    role: string,
    offset: number,
    limit: number
  ): Observable<any> {
    return this.get(
      `/users/players?offset=${offset}&limit=${limit}&role=OTHER&verificationStatus=VERIFIED`
    );
  }

  getPlayersVerified(
    role: string,
    offset: number,
    limit: number
  ): Observable<any> {
    return this.get(
      `/users?offset=${offset}&limit=${limit}&role=PLAYER&verificationStatus=VERIFIED`
    );
  }

/*=============================================
=            All Persona's APi            =
=============================================*/

/*----------  get players  ----------*/
  getPlayer(offset : number , limit : number , verificationStatus? : string , name? : string ): Observable<any> {
    let param = new HttpParams();
    if(verificationStatus) { 

      param = param.append('verificationStatus', verificationStatus.toString());
    }
    if(name) { 

      param = param.append('name', name.toString());
    }
    return this.get(`/users/players?offset=${offset}&limit=${limit}&role=PLAYER` , param);
  }

  
  /*----------  get Other Persona's  ----------*/
    getPersona(offset : number , limit : number , role : string , verificationStatus? : string , name? : string , preset? : string ): Observable<any> {
    let param = new HttpParams();
    if(verificationStatus) { 

      param = param.append('verificationStatus', verificationStatus.toString());
    }
    if(name) { 

      param = param.append('name', name.toString());
    }
    if(preset) { 

      param = param.append('preset', preset.toString());
    }
    return this.get(`/users?offset=${offset}&limit=${limit}&role=${role}` , param);
  }
  



/*=====  End of All Persona's APi  ======*/


/*=============================================
=            Go-reach Api            =
=============================================*/
  getProfileOffers(limit: number = 20, offset: number = 0): Observable<any> {
    return this.get(`/offers/profile?limit=${limit}&offset=${offset}`);
  }


    getOfferDetailsById(id: string): Observable<any> {
    return this.get(`/offers/profile/${id}`);
  }

  pricingUnits(): Observable<any> {
    return this.get(`/offers/pricing-table/raw`);
  } 
   
  addPricingUnit(data: any): Observable<any> {
    return this.post(`/offers/pricing-table`, data);
  }

  deletePricingUnit(id: string): Observable<any> {
    return this.delete(`/offers/pricing-table/raw/${id}`);
  }



/*=====  End of Go-reach Api  ======*/




  getOtherUsers(offset : number , limit : number , subRole? : string ): Observable<any> {
    let params = new HttpParams()
    if(subRole){ 
      params = params.append('subRole', subRole);
    }

    return this.get(`/users?offset=${offset}&limit=${limit}&role=OTHER` , params); 
  }

  getScoutsVerified(
    role: string,
    offset: number,
    limit: number
  ): Observable<any> {
    return this.get(
      `/users?offset=${offset}&limit=${limit}&role=SCOUT&verificationStatus=VERIFIED`
    );
  }

  getAgentsVerified(
    role: string,
    offset: number,
    limit: number
  ): Observable<any> {
    return this.get(
      `/users?offset=${offset}&limit=${limit}&role=AGENT&verificationStatus=VERIFIED`
    );
  }

  getCoachsVerified(
    role: string,
    offset: number,
    limit: number
  ): Observable<any> {
    return this.get(
      `/users?offset=${offset}&limit=${limit}&role=COACH&verificationStatus=VERIFIED`
    );
  }

  getAdminsVerified(
    role: string,
    offset: number,
    limit: number
  ): Observable<any> {
    return this.get(
      `/users?offset=${offset}&limit=${limit}&role=ADMIN&verificationStatus=VERIFIED`
    );
  }

  // getUsereditPending(id: string): Observable<any> {
  //   return this.get(`/users/players?offset=0&limit=20&role=PLAYER&verificationStatus=PENDING` );
  // }
  getUsereditPending(
    offset: number,
    limit: number
  ): Observable<any> {
    return this.get(
      `/users/players?offset=${offset}&limit=${limit}&verificationStatus=PENDING`
    );
  }

  getGuardiansPending(
    role: string,
    offset: number,
    limit: number
  ): Observable<any> {
    return this.get(
      `/users/players?offset=${offset}&limit=${limit}&role=OTHER&verificationStatus=PENDING`
    );
  }

  getPlayersPending(
    role: string,
    offset: number,
    limit: number
  ): Observable<any> {
    return this.get(
      `/users?offset=${offset}&limit=${limit}&role=PLAYER&verificationStatus=PENDING`
    );
  }

  getScoutsPending(
    role: string,
    offset: number,
    limit: number
  ): Observable<any> {
    return this.get(
      `/users?offset=${offset}&limit=${limit}&role=SCOUT&verificationStatus=PENDING`
    );
  }

  getAgentsPending(
    role: string,
    offset: number,
    limit: number
  ): Observable<any> {
    return this.get(
      `/users?offset=${offset}&limit=${limit}&role=AGENT&verificationStatus=PENDING`
    );
  }

  getCoachsPending(
    role: string,
    offset: number,
    limit: number
  ): Observable<any> {
    return this.get(
      `/users?offset=${offset}&limit=${limit}&role=COACH&verificationStatus=PENDING`
    );
  }

  getAdminsPending(
    role: string,
    offset: number,
    limit: number
  ): Observable<any> {
    return this.get(
      `/users?offset=${offset}&limit=${limit}&role=ADMIN&verificationStatus=PENDING`
    );
  }
  // fetchUserUnVerified(id: string): Observable<any> {
  //   return this.get(`/users/players?offset=0&limit=20&role=PLAYER&verificationStatus=UNVERIFIED` );
  // }
  fetchUserUnVerified(
    role: string,
    offset: number,
    limit: number
  ): Observable<any> {
    return this.get(
      `/users/players?offset=${offset}&limit=${limit}&role=PLAYER&verificationStatus=UNVERIFIED`
    );
  }

  fetchPlayerUnVerified(
    role: string,
    offset: number,
    limit: number
  ): Observable<any> {
    return this.get(
      `/users?offset=${offset}&limit=${limit}&role=PLAYER&verificationStatus=UNVERIFIED`
    );
  }

  fetchGuardiansUnVerified(
    role: string,
    offset: number,
    limit: number
  ): Observable<any> {
    return this.get(
      `/users/players?offset=${offset}&limit=${limit}&role=OTHER&verificationStatus=UNVERIFIED`
    );
  }

  fetchScoutsUnVerified(
    role: string,
    offset: number,
    limit: number
  ): Observable<any> {
    return this.get(
      `/users/players?offset=${offset}&limit=${limit}&role=SCOUT&verificationStatus=UNVERIFIED`
    );
  }

  fetchAgentsUnVerified(
    role: string,
    offset: number,
    limit: number
  ): Observable<any> {
    return this.get(
      `/users/players?offset=${offset}&limit=${limit}&role=AGENT&verificationStatus=UNVERIFIED`
    );
  }

  fetchCoachsUnVerified(
    role: string,
    offset: number,
    limit: number
  ): Observable<any> {
    return this.get(
      `/users/players?offset=${offset}&limit=${limit}&role=COACH&verificationStatus=UNVERIFIED`
    );
  }

  fetchAdminsUnVerified(
    role: string,
    offset: number,
    limit: number
  ): Observable<any> {
    return this.get(
      `/users/players?offset=${offset}&limit=${limit}&role=ADMIN&verificationStatus=UNVERIFIED`
    );
  }

  // end of users list
  getUserNetwork(
    user_id: number = 0,
    limit: number = 50,
    offset: number = 0
  ): Observable<any> {
    return this.get(
      `/users/${user_id}/network?limit=${limit}&offset=${offset}`
    );
  }

  getUserActivities(
    userId: number = 0,
    limit: number = 10,
    game: boolean = false
  ): Observable<any> {
    return this.get(
      `/users/${userId}/activities/basic-post?limit=${limit}&game=${game}`
    );
  }

  
  /*=============================================
  =            Payment Roles API            =
  =============================================*/
  getPaymentRoles() : Observable<any>{
    return this.get('/api/payments/roles');
  }
  

  addPaymentRole(data: any): Observable<any> {
    return this.post('/api/payments/roles', data);
  }

  deletePaymentRole(id: any): Observable<any> {
    return this.delete(`/api/payments/roles/${id}`);
  }

  finance(): Observable<any> {
    return this.get('/users/wallet/finance');
  }

  refund(): Observable<any> {
    return this.get('/users/refund/finance')
  }

 transferedTransactions(userId: number, limit: number = 10, offset: number = 0): Observable<any> {
  return this.get(`/provider-payouts?userId=${userId}&limit=${limit}&offset=${offset}`);
}
  
  /*=====  End of Payment Roles API  ======*/
  


  
  /*----------  Delete Users  ----------*/
  deleteUser(id: string): Observable<any> {
   return this.delete(`/users/${id}`);
  }

  
  /*----------  Users Notifications   ----------*/
  userNotifications(id: string): Observable<any> {
    return this.get(`/users/${id}/notifications/?category=general`)
  }
  
  

  getchallenges(limit: any, offset: any): Observable<any> {
    return this.get(
      `/challenges/admin?limit=${limit}&offset=${offset}&isDeleted=false&search= `
    );
  }

  // Sending Notifications
  sendNotification(data: any): Observable<any> {
    return this.post(`/admin/notifications`, data);
  }


  
  /*=============================================
  =            Transactions Api            =
  =============================================*/
    /*----------  Get all Transactions  ----------*/
  transactions(limit:any , offset:any):Observable<any>{
    return this.get(`/payment-transactions?limit=${limit}&offset=${offset}&type=goservice_contract`);
  }

  
  /*----------  Get User Transaction by id  ----------*/
  userTransactions(id :any) : Observable<any> {
   return this.get(`/api/payment/transactions?userId=${id}`);
  }

  
  /*----------  Get Invoice details  ----------*/
  invoiceDetails(transaction_id:any):Observable<any>{
    return this.get(`/api/payment/link/transactions/${transaction_id}/invoice`);
  }

  transactionspaidDetails(transactiond_id :any) : Observable<any> {
    return this.get(`/payment-transactions/${transactiond_id}`);
  }

  transactionsReceivedDetails(transactiond_id :any) : Observable<any> {
    return this.get(`/api/wallets/transactions/${transactiond_id}`);
  }
  
  
  
  
  /*=====  End of Transactions Api  ======*/
  
  
  
  
  /*----------  Payment Details   ----------*/
  paymentDetails(id:any):Observable<any>{
    return this.get(`/users/${id}/payout-accounts`);
  }
  
  
  /*----------  Get pending Users details  ----------*/
  PendingUserDetails(userId:any): Observable<any> {
    return this.get(`/users/${userId}/form`);
    
  }

  
  /*----------  Accepting User Verification Requesti  ----------*/
  acceptUser(id:any): Observable<any> {
    return this.patch(`/users/${id}/accept` , {});
  }
  
  
  /*----------  Send Rejection Reason  ----------*/
  Rejection(id:any ,data:any): Observable<any> {
    return this.patch(`/users/${id}/decline` , data);
  }
  
  

  /*----------  All Services  ----------*/
  getAllServices(limit: any, offset: any, searchValue: any): Observable<any> {
    return this.get(
      `/api/services/trending?limit=${limit}&offset=${offset}&search=${searchValue}`
    );
  }
  
  
  /*----------  Playing Level  ----------*/
  playingLevel():Observable<any>{
    return this.get(`/experiences/listPlayingLevels`);
  }
  
  
  /*----------  Get Skills  ----------*/
  skills():Observable<any>{
    return this.get(`/skills/adminFilter`)
  }
  

  

/*----------  Advanced Search API  ----------*/ 
advancedSearch(
  playerName?: any,
  gender?: any,
  country?: any,
  cities?: any,
  primaryPosition?: any,
  strongFoot?: any,
  hasAgent?: any,
  hasCertification?: any,
  currentlyPlaying?: any,
  hasNationalExp?: any,
  secondaryPositions?: any
): Observable<any> {
  let params = new HttpParams()
    .set('offset', '0')
    .set('limit', '20')
    .set('role', 'PLAYER');


  if (playerName) {
    params = params.set('playerName', playerName);
  }
  if (gender) {
    params = params.set('gender', gender);
  }
  if (country) {
    params = params.set('country', country);
  }
  if (cities) {
    params = params.set('cities[]', cities);
  }
  if (primaryPosition) {
    params = params.set('primaryPosition', primaryPosition);
  }
  if (strongFoot) {
    params = params.set('strongFoot[]', strongFoot);
  }
  if (hasAgent !== undefined) {
    params = params.set('hasAgent', String(hasAgent));
  }
  if (hasCertification !== undefined) {
    params = params.set('hasCertification', String(hasCertification));
  }
  if (currentlyPlaying !== undefined) {
    params = params.set('currentlyPlaying', String(currentlyPlaying));
  }
  if (hasNationalExp !== undefined) {
    params = params.set('hasNationalExp', String(hasNationalExp));
  }
  if (secondaryPositions) {
    params = params.set('secondaryPositions[]', secondaryPositions);
  }

  return this.get(`/users/players`, params);
}



  /*----------  All Trending  ----------*/
  getTrending(limit: any, offset: any, searchValue: any): Observable<any> {
    return this.get(
      `/api/services/limit=${limit}&offset=${offset}&search=${searchValue}`
    );
  }

  /*----------  All Services  ----------*/
  getService(limit: any, offset: any, searchValue: any): Observable<any> {
    return this.get(
      `/api/services?limit=${limit}&offset=${offset}&search=${searchValue}`
    );
  }

  /*----------  All TopServiceProviders  ----------*/
  getTopServiceProviders(
    limit: any,
    offset: any,
    searchValue: any
  ): Observable<any> {
    return this.get(
      `/api/services/top-service-providers?limit=${limit}&offset=${offset}&search=${searchValue}`
    );
  }

  /*----------  Get service by userID   ----------*/
  serviceByUserId(userId: any): Observable<any> {
    return this.get(`/api/users/${userId}/services`);
  }
  /*----------  Get service details by ID  ----------*/
  serviceById(serviceId: any): Observable<any> {
    return this.get(`/api/services/${serviceId}`);
  }

  /*----------  Get posts by userId  ----------*/
  getPostsById(id: any, limit: any, offset: any): Observable<any> {
    return this.get(
      `/users/${id}/activities/basic-post?limit=${limit}&offset=${offset}`
    ).pipe(
      retry(3)
    );
  }

  
  /*----------  Service Requesters  ----------*/
  serviceRequesters(serviceId: any):Observable<any>{
    return this.get(`/api/services/${serviceId}/subscriptions`)
  }

  

/*=============================================
=            Gino API            =
=============================================*/
  /*----------  Gino Packages  ----------*/
  ginoPackages() : Observable<any> {
    return this.get(`/gino-tokens/addons`)
  }

  
  /*----------  Add Gino Package  ----------*/
  addGinoPackage(data : any) : Observable<any> {
    return this.post(`/gino-tokens/addons`, data)
  }

  
  /*----------  Delete Gino Package  ----------*/
  deleteGinoPackage(id: any) : Observable<any> {
    return this.delete(`/gino-tokens/addons/${id}`)
  }

  
  /*----------  Gino Subscribers  ----------*/
  ginoSubscribers(page: any , limit : any): Observable<any> {
    return this.get(`/gino-tokens/addons/purchases?page=${page}&limit=${limit}`)
  }

  ginoAddOns() : Observable<any> {
    return this.get(`/gino-tokens/addons`)
  }
  
  
  ginoCheckout(data : any) : Observable<any> {
    return this.post(`/gino-tokens/addons/checkout`, data)
  }

  ginoPackageById(id : any) : Observable<any> {
    return this.get(`/gino-tokens/addons/${id}`)
  }

  editGinoPackage(id : any , data : any) : Observable<any> {
    return this.patch(`/gino-tokens/addons/${id}`, data)
  }


/*=====  End of Gino API  ======*/


  
  

  
  /*=============================================
  =            Wallets Api            =
  =============================================*/
  
  /*----------  get all user wallets  ----------*/
  getAllWallets(options?: { usersId?: any; currency?: any }): Observable<any> {
    let params = new HttpParams();
  
    if (options?.usersId) {
      params = params.set('usersId', options.usersId);
    }
    if (options?.currency) {
      params = params.set('currency', options.currency);
    }
  
    return this.get('/api/wallets', params).pipe(
      retry(3)
    );
  }

  
  
  /*----------  get all wallet insights  ----------*/
  walletInsights(): Observable<any> {
    return this.get('/api/wallets/insights').pipe(
      retry(3)
    );
  }


/*----------  wallet insights for each user  ----------*/
userWalletInsights(id:any): Observable<any> {
  return this.get(`/api/wallets/insights?userId=${id}`)
}


/*----------  Wallet Summary  ----------*/
walletSummary(userId :any): Observable<any> {
  return this.get(`/users/wallet/summary?userId=${userId}`).pipe(
    retry(3)
  );
}


/*----------  Get User Bills  ----------*/
userBills(id:any) : Observable<any> {
  return this.get(`/users/${id}/payment-transactions`).pipe(
    retry(3)
  );
}


/*----------  Get User transactions  ----------*/
userTransactionsById(limit : any , offset : any , user_id:any ) : Observable<any> { 
  return this.get(`/payment-transactions?limit=${limit}&offset=${offset}&userId=${user_id}`)
}





/*----------  Transaction of each user  ----------*/
userWalletTransactions(id:any): Observable<any> {
  return this.get(`/api/wallets/transactions?userId=${id}`)
}


/*----------  Requested Transactions  ----------*/
requestedTransactions(): Observable<any> {
  return this.get('/api/wallets/transactions?status=pending').pipe(
    retry(3)
  );
}


/*----------  Accepting or rejecting transaction  ----------*/
transactionStatus(id : any , status : any) : Observable<any> {
  return this.patch(`/api/wallets/transactions/${id}/status` , {status : status}).pipe(
    retry(3)
  );
}




  

  
  
  /*=====  End of Wallets Api  ======*/
  

  
  /*=============================================
  =            Privileges API            =
  =============================================*/
getPrivileges(): Observable<any> {
  return this.get(`/admin-acl`)
}


getDepartments(): Observable<any> {
  return this.get(`/admin-acl/departments`)
}

addDepartment(data : any): Observable<any> {
  return this.post(`/admin-acl/departments`, data)
}

deleteDepartment(id : any): Observable<any> {
  return this.delete(`/admin-acl/departments/${id}`)
}

departmentDetails(id : any): Observable<any> {
  return this.get(`/admin-acl/departments/${id}`)
}

editDepartment(id : any , data : any): Observable<any> {
  return this.patch(`/admin-acl/departments/${id}`, data)
}

getRoles(): Observable<any> {
  return this.get(`/admin-acl/roles`)
}

addUserRole(data : any): Observable<any> {
  return this.post(`/admin-acl/roles`, data)
}

getTabs(): Observable<any> {
  return this.get(`/admin-acl/tabs`)
}

addTabs(data : any): Observable<any> {
  return this.post(`/admin-acl/tabs`, data)
}

deleteTab(id : any): Observable<any> {
  return this.delete(`/admin-acl/tabs/${id}`)
}

getActions(): Observable<any> {
  return this.get(`/admin-acl/actions`)
}
  
addActions(data : any): Observable<any> {
  return this.post(`/admin-acl/actions`, data)
}

editAction(id : any , data : any): Observable<any> {
  return this.patch(`/admin-acl/actions/${id}`, data)
}

deleteAction(id : any): Observable<any> {
  return this.delete(`/admin-acl/actions/${id}`)
}

actionDetails(id : any): Observable<any> {
  return this.get(`/admin-acl/actions/${id}`)
}
  
  /*=====  End of Privileges API  ======*/
  
  
  
  
  
  /*=============================================
  =            Payment Link             =
  =============================================*/
  
  /*----------  Payment Link  ----------*/
getPaymentLink(): Observable<any> {
  return this.get(`/api/admin/payment/link`).pipe(
    retry(3)
  );
}


/*----------  Payment Link Transactions  ----------*/
getPaymentTransactions(): Observable<any> {
  return this.get(`/api/admin/payment/link/history`).pipe(
    retry(3)
  );
}


  /*----------  Creating payment link account    ----------*/
  paymentAccount(data: any): Observable<any> {
    return this.post(`/api/payout-accounts`, data).pipe(
      retry(3)
    );
  }

  paymentLinkTransactions(id : any): Observable<any> {
  return this.get(`/api/admin/payment/link/${id}/transactions`).pipe(
    retry(3)
  );
}
  
  
  /*=====  End of Payment Link   ======*/
  
  

  /*----------  user stats  ----------*/
  getUserStats(): Observable<any> {
    return this.get(`/users/statistics`);
  }

  

  

  // Get all analytics cards
  getAllCards(): Observable<any> {
    return this.http.get(
      `https://bi.goalngo.com/api/public/dashboard/b69791f8-7308-463f-979b-90bffe1e32b3`,
      { headers: { Authorization: this.Header() ? this.Header() : "" } }
    );
  }
  // Get data for the last registrated users last 24 hours
  registrationPastDay(): Observable<any> {
    return this.http.get(
      `https://bi.goalngo.com/api/public/dashboard/b69791f8-7308-463f-979b-90bffe1e32b3/dashcard/29/card/5?parameters=%5B%5D `,
      { headers: { Authorization: this.Header() ? this.Header() : "" } }
    );
  }

  //new registration per month
  registrationPerMonth(): Observable<any> {
    return this.http.get(
      "https://bi.goalngo.com/api/public/dashboard/b69791f8-7308-463f-979b-90bffe1e32b3/dashcard/31/card/10?parameters=%5B%5D ",
      { headers: { Authorization: this.Header() ? this.Header() : "" } }
    );
  }
  // Most Viewed Profile
  mostViewedProfile(): Observable<any> {
    return this.http.get(
      "https://bi.goalngo.com/api/public/dashboard/b69791f8-7308-463f-979b-90bffe1e32b3/dashcard/32/card/29?parameters=%5B%5D",
      { headers: { Authorization: this.Header() ? this.Header() : "" } }
    );
  }

  // profile view segementation
  profileSegementation(): Observable<any> {
    return this.http.get(
      "https://bi.goalngo.com/api/public/dashboard/b69791f8-7308-463f-979b-90bffe1e32b3/dashcard/33/card/30?parameters=%5B%5D ",
      { headers: { Authorization: this.Header() ? this.Header() : "" } }
    );
  }
  // Fetching data of the last registered users by role last week
  registrationByRole(): Observable<any> {
    return this.http.get(
      "https://bi.goalngo.com/api/public/dashboard/b69791f8-7308-463f-979b-90bffe1e32b3/dashcard/36/card/21?parameters=%5B%5D ",
      { headers: { Authorization: this.Header() ? this.Header() : "" } }
    );
  }

  // Total number of posts last 7 days
  weeklyposts(): Observable<any> {
    return this.http.get(
      "https://bi.goalngo.com/api/public/dashboard/b69791f8-7308-463f-979b-90bffe1e32b3/dashcard/46/card/32?parameters=%5B%5D  ",
      { headers: { Authorization: this.Header() ? this.Header() : "" } }
    );
  }

  // Number of repeated logged in users last month by roles
  repeatedLoginsLastMonth(): Observable<any> {
    return this.chartFunction(
      "https://bi.goalngo.com/api/public/dashboard/b69791f8-7308-463f-979b-90bffe1e32b3/dashcard/60/card/42?parameters=%5B%5D "
    );
  }
  // "No. of REPEATED logged in Users in the past 7 days by Country"
  repeatedLoginsLastWeek(): Observable<any> {
    return this.chartFunction(
      "https://bi.goalngo.com/api/public/dashboard/b69791f8-7308-463f-979b-90bffe1e32b3/dashcard/61/card/43?parameters=%5B%5D "
    );
  }

  // "Clubs Added by 3 users at least by persona"
  clubsAddedByUsers(): Observable<any> {
    return this.chartFunction(
      "https://bi.goalngo.com/api/public/dashboard/b69791f8-7308-463f-979b-90bffe1e32b3/dashcard/64/card/27?parameters=%5B%5D"
    );
  }

  // clubs added by 1 user at least by persona
  clubsAddedByOneUser(): Observable<any> {
    return this.chartFunction(
      "https://bi.goalngo.com/api/public/dashboard/b69791f8-7308-463f-979b-90bffe1e32b3/dashcard/106/card/85?parameters=%5B%5D "
    );
  }
  // "No. of REPEATED logged in Users in the past 7 days by day and by Role"
  repeatedLoginsByRole(): Observable<any> {
    return this.chartFunction(
      "https://bi.goalngo.com/api/public/dashboard/b69791f8-7308-463f-979b-90bffe1e32b3/dashcard/107/card/88?parameters=%5B%5D"
    );
  }

  // Games by Country
  gamesByCountry(): Observable<any> {
    return this.chartFunction(
      "https://bi.goalngo.com/api/public/dashboard/b69791f8-7308-463f-979b-90bffe1e32b3/dashcard/110/card/93?parameters=%5B%5D "
    );
  }
  // Games Posts by Buckets
  gamesByBuckets(): Observable<any> {
    return this.chartFunction(
      "https://bi.goalngo.com/api/public/dashboard/b69791f8-7308-463f-979b-90bffe1e32b3/dashcard/112/card/58?parameters=%5B%5D"
    );
  }
  // "Challenges number of joiners by buckets"
  challengesByBuckets(): Observable<any> {
    return this.chartFunction(
      "https://bi.goalngo.com/api/public/dashboard/b69791f8-7308-463f-979b-90bffe1e32b3/dashcard/113/card/59?parameters=%5B%5D"
    );
  }

  fetchAnalytics(): Observable<any> {
    const headers = this.createHeaders();
    const token = headers.get("Authorization"); // Extract token

    return from(
      fetch(
        "https://bi.goalngo.com/api/public/dashboard/b69791f8-7308-463f-979b-90bffe1e32b3/dashcard/27/card/8?parameters=%5B%5D",
        {
          method: "GET",
          headers: { Authorization: token ? token : "" }, // Ensure token is properly passed
        }
      ).then((response) => response.json()) // Convert response to JSON
    );
  }

  fetchAnalyticsByCountry(): Observable<any> {
    const headers = this.createHeaders();
    const token = headers.get("Authorization"); // Extract token

    return from(
      fetch(
        "https://bi.goalngo.com/api/public/dashboard/b69791f8-7308-463f-979b-90bffe1e32b3/dashcard/28/card/9?parameters=%5B%5D",
        {
          method: "GET",
          headers: { Authorization: token ? token : "" }, // Ensure token is properly passed
        }
      ).then((response) => response.json()) // Convert response to JSON
    );
  }

  verificationStatus(): Observable<any> {
    const headers = this.createHeaders();
    const token = headers.get("Authorization"); // Extract token
    return this.http.get(
      "https://bi.goalngo.com/api/public/dashboard/b69791f8-7308-463f-979b-90bffe1e32b3/dashcard/30/card/20?parameters=%5B%5D ",
      { headers: { Authorization: token ? token : "" } }
    );
  }
  // Fetching sub roles analytics
  subRoles(): Observable<any> {
    const headers = this.createHeaders();
    const token = headers.get("Authorization"); // Extract token
    return this.http.get(
      "https://bi.goalngo.com/api/public/dashboard/b69791f8-7308-463f-979b-90bffe1e32b3/dashcard/105/card/84?parameters=%5B%5D",
      { headers: { Authorization: token ? token : "" } }
    );
  }
  // Fetching friends counts analytics
  friendsCount(): Observable<any> {
    return this.http.get(
      "https://bi.goalngo.com/api/public/dashboard/b69791f8-7308-463f-979b-90bffe1e32b3/dashcard/112/card/58?parameters=%5B%5D",
      { headers: { Authorization: this.Header() ? this.Header() : "" } }
    );
  }

  postsCount(): Observable<any> {
    return this.http.get(
      "https://bi.goalngo.com/api/public/dashboard/b69791f8-7308-463f-979b-90bffe1e32b3/dashcard/35/card/14?parameters=%5B%5D",
      { headers: { Authroization: this.Header() ? this.Header() : "" } }
    );
  }
}
